import { useStoredState } from "@/hooks/useStoredState";
import { ToolShell } from "@/components/tools/ToolShell";
import { AutoTextarea, Pills, TextField } from "@/components/tools/fields";
import { SubHead } from "@/components/tools/blocks";
import { TickList } from "@/components/tools/ProvisionReview";

export const QS = {
  health: { id: "qs-health", n: "01", title: "SENDCO Health Check", blurb: "Review your current position. Rate each area as Significant Concern, Developing or Established." },
  provision: { id: "qs-provision", n: "02", title: "SEND Provision Review", blurb: "Identify strengths, gaps and priorities for improvement." },
  ehcp: { id: "qs-ehcp", n: "03", title: "EHCP Annual Review Checklist", blurb: "Stay organised and track essential review requirements." },
  parent: { id: "qs-parent", n: "04", title: "Parent Partnership Reflection", blurb: "Evaluate how effectively your school engages with families. Rate each statement from 1 to 5." },
  plan: { id: "qs-plan", n: "05", title: "SEND Action Planner", blurb: "Turn priorities into clear actions and measurable outcomes." },
  notes: { id: "qs-notes", n: "06", title: "SEND Improvement Notes", blurb: "Capture what your reflection has identified, and what you need to act on it." },
  resources: { id: "qs-resources", n: "07", title: "Resource Directory", blurb: "More support from The SEND Accountability Project." },
};

const AREAS = ["Leadership Support", "SEND Strategy", "SEND Processes", "Parent Partnership", "EHCP Management", "Staff Confidence", "SEND Data & Monitoring", "Provision Mapping", "Outcomes Tracking", "Attendance & Inclusion", "Transition Planning"];
const RAG = [
  { v: "red", label: "Significant Concern", tone: "red" },
  { v: "amber", label: "Developing", tone: "amber" },
  { v: "green", label: "Established", tone: "green" },
];
const useSet = (setD) => (k) => (v) => setD((x) => ({ ...x, [k]: v }));

export const QsHealth = () => {
  const [d, setD, reset] = useStoredState("qs-health-v3", { rag: {} });
  const set = useSet(setD);
  return (
    <ToolShell id={QS.health.id} tool={QS.health} onReset={reset}>
      <ol className="divide-y divide-line border-y border-line">
        {AREAS.map((a, i) => (
          <li key={a} className="avoid-break flex flex-col gap-3 py-4 lg:flex-row lg:items-center lg:justify-between">
            <p className="font-bold">{a}</p>
            <Pills legend={`Rating for ${a}`} name={`qs-h-${i}`} options={RAG} value={d.rag?.[i]} onChange={(v) => setD((x) => ({ ...x, rag: { ...x.rag, [i]: v } }))} testid={`qs-health-${i}`} />
          </li>
        ))}
      </ol>
      <div className="mt-6 grid gap-4 md:grid-cols-3">
        <AutoTextarea id="qs-h-well" label="What is currently working well?" rows={4} value={d.well} onChange={set("well")} testid="qs-health-well" />
        <AutoTextarea id="qs-h-attention" label="What requires attention?" rows={4} value={d.attention} onChange={set("attention")} testid="qs-health-attention" />
        <AutoTextarea id="qs-h-top" label="What are your top three SEND priorities?" rows={4} value={d.top} onChange={set("top")} testid="qs-health-top" />
      </div>
    </ToolShell>
  );
};

export const QsProvision = () => {
  const [d, setD, reset] = useStoredState("qs-provision-v2", {});
  const set = useSet(setD);
  return (
    <ToolShell id={QS.provision.id} tool={QS.provision} onReset={reset}>
      <p className="mb-4 font-bold">Reflect on current provision</p>
      <div className="grid gap-4 md:grid-cols-2">
        <AutoTextarea id="qs-p-str" label="Strengths: what aspects of SEND provision are working effectively?" rows={4} value={d.strengths} onChange={set("strengths")} testid="qs-prov-strengths" />
        <AutoTextarea id="qs-p-dev" label="Areas for development: which aspects would benefit from review or improvement?" rows={4} value={d.development} onChange={set("development")} testid="qs-prov-development" />
        <AutoTextarea id="qs-p-imm" label="Immediate actions: what actions could be taken within the next term?" rows={4} value={d.immediate} onChange={set("immediate")} testid="qs-prov-immediate" />
        <AutoTextarea id="qs-p-long" label="Longer-term development priorities" rows={4} value={d.longer} onChange={set("longer")} testid="qs-prov-longer" />
      </div>
    </ToolShell>
  );
};

const STAGES = [
  { id: "before", title: "Before the Review", items: ["Review date confirmed", "Parents/carers invited", "Child or young person's views gathered", "Professional reports requested", "Reports received", "Documentation circulated", "Venue/meeting arrangements confirmed", "Attendees confirmed"] },
  { id: "during", title: "During the Review", items: ["Progress reviewed", "Outcomes discussed", "Current provision considered", "Parent views recorded", "Child views recorded", "Recommendations agreed", "Future support needs discussed", "Actions assigned"] },
  { id: "after", title: "After the Review", items: ["Notes completed", "Local Authority notified", "Submission completed", "Actions tracked", "Review date noted"] },
];

export const QsEhcp = () => {
  const [d, setD, reset] = useStoredState("qs-ehcp-v3", {});
  return (
    <ToolShell id={QS.ehcp.id} tool={QS.ehcp} onReset={reset}>
      {STAGES.map((s, i) => (
        <div key={s.id}>
          <SubHead letter={String.fromCharCode(65 + i)} title={s.title} />
          <TickList name={`qs-ehcp-${s.id}`} items={s.items} value={d[s.id]} onChange={(v) => setD((x) => ({ ...x, [s.id]: v }))} />
        </div>
      ))}
      <div className="mt-6"><AutoTextarea id="qs-ehcp-notes" label="Notes" rows={3} value={d.notes} onChange={(v) => setD((x) => ({ ...x, notes: v }))} testid="qs-ehcp-notes" /></div>
    </ToolShell>
  );
};

const STATEMENTS = ["Parents feel listened to", "Communication is timely", "Information is accessible", "Meetings feel collaborative", "Concerns are addressed appropriately", "Parents understand provision arrangements"];
const OPTS = [1, 2, 3, 4, 5].map((n) => ({ v: n, label: String(n) }));

export const QsParent = () => {
  const [d, setD, reset] = useStoredState("qs-parent-v3", { s: {} });
  const set = useSet(setD);
  const total = STATEMENTS.reduce((a, _, i) => a + (d.s?.[i] || 0), 0);
  return (
    <ToolShell id={QS.parent.id} tool={QS.parent} onReset={reset}>
      <ol className="divide-y divide-line border-y border-line">
        {STATEMENTS.map((t, i) => (
          <li key={t} className="avoid-break flex flex-col gap-3 py-4 md:flex-row md:items-center md:justify-between">
            <p className="font-bold">{t}</p>
            <Pills legend={`Score for: ${t}`} name={`qs-pp-${i}`} options={OPTS} value={d.s?.[i]} onChange={(v) => setD((x) => ({ ...x, s: { ...x.s, [i]: v } }))} testid={`qs-parent-${i}`} />
          </li>
        ))}
      </ol>
      <p className="mt-4 font-bold" aria-live="polite" data-testid="qs-parent-total">Total: {total} / {STATEMENTS.length * 5}</p>
      <div className="mt-4 grid gap-4 md:grid-cols-3">
        <AutoTextarea id="qs-pp-well" label="What are we doing well?" rows={3} value={d.well} onChange={set("well")} testid="qs-parent-well" />
        <AutoTextarea id="qs-pp-improve" label="What could improve?" rows={3} value={d.improve} onChange={set("improve")} testid="qs-parent-improve" />
        <AutoTextarea id="qs-pp-actions" label="Planned actions" rows={3} value={d.actions} onChange={set("actions")} testid="qs-parent-actions" />
      </div>
    </ToolShell>
  );
};

export const QsPlan = () => {
  const [d, setD, reset] = useStoredState("qs-plan-v2", {});
  const set = useSet(setD);
  return (
    <ToolShell id={QS.plan.id} tool={QS.plan} onReset={reset}>
      <div className="grid gap-4 md:grid-cols-2">
        <AutoTextarea id="qs-plan-area" label="Priority area" value={d.area} onChange={set("area")} testid="qs-plan-area" />
        <AutoTextarea id="qs-plan-current" label="Current position" value={d.current} onChange={set("current")} testid="qs-plan-current" />
        <AutoTextarea id="qs-plan-outcome" label="Desired outcome" value={d.outcome} onChange={set("outcome")} testid="qs-plan-outcome" />
        <AutoTextarea id="qs-plan-actions" label="Actions required" rows={3} value={d.actions} onChange={set("actions")} testid="qs-plan-actions" />
        <TextField id="qs-plan-lead" label="Lead person" value={d.lead} onChange={set("lead")} testid="qs-plan-lead" />
        <TextField id="qs-plan-review" label="Review date" type="date" value={d.review} onChange={set("review")} testid="qs-plan-review" />
        <div className="md:col-span-2"><AutoTextarea id="qs-plan-success" label="Success measures" rows={3} value={d.success} onChange={set("success")} testid="qs-plan-success" /></div>
      </div>
    </ToolShell>
  );
};

export const QsNotes = () => {
  const [d, setD, reset] = useStoredState("qs-notes", {});
  const set = useSet(setD);
  return (
    <ToolShell id={QS.notes.id} tool={QS.notes} onReset={reset}>
      <div className="grid gap-4 md:grid-cols-3">
        <AutoTextarea id="qs-n-key" label="Key areas identified through reflection" rows={5} value={d.key} onChange={set("key")} testid="qs-notes-key" />
        <AutoTextarea id="qs-n-res" label="Resources required" rows={5} value={d.resources} onChange={set("resources")} testid="qs-notes-resources" />
        <AutoTextarea id="qs-n-part" label="Partnerships and support" rows={5} value={d.partners} onChange={set("partners")} testid="qs-notes-partners" />
      </div>
    </ToolShell>
  );
};
